import React, { useEffect, useState } from "react";
import { PlxsCallout, PlxsText, PlxsIcon } from "@plexlogic/plexus-ui";
import useUploadStore, {
  useHasUploadedFile,
  useHasCsvConfirmationSetting,
  useIsHeaderRow,
} from "./useUploadStore";
import { toSentenceCase } from "../../utils/stringUtils";

import FilePreview from "./FilePreview";
import HeaderRowQuestion from "./HeaderRowQuestion";
import ParticipantIdentificationQuestion from "./ParticipantIdentificationQuestion";
import AdvancedEntriesUpload from "./AdvancedEntriesUpload";
import WizardFooterNav from "../WizardFooterNav";
import WizardStepHeader from "../WizardStepHeader";
import SmartUploadComponent from "./SmartUploadComponent";

interface UploadEntriesStepProps {
  entryMethod: "simple" | "standard" | "advanced";
  drawId?: string;
  onNext: () => void;
  onBack: () => void;
  isLocked?: boolean;
}

const UploadEntriesStep: React.FC<UploadEntriesStepProps> = ({
  entryMethod,
  drawId,
  onNext,
  onBack,
  isLocked = false,
}) => {
  const status = useUploadStore((state: any) => state.status);
  const entriesUpload = useUploadStore((state: any) => state.entriesUpload);
  const deleteFile = useUploadStore((state) => state.deleteFile);
  const commitToDraw = useUploadStore((state) => state.commitToDraw);
  const reset = useUploadStore((state) => state.reset);
  const setCurrentDrawId = useUploadStore((state) => state.setCurrentDrawId);
  const headerRowAnswer = useIsHeaderRow() ?? null;

  const [isAdvancedFormValid, setIsAdvancedFormValid] =
    useState<boolean>(false);

  const isUploaded = status === "success";
  const isUploading = status === "uploading";
  const hasError = status === "error";
  const hasUploadedFile = useHasUploadedFile();
  const hasCsvEntryConfirmationSetting = useHasCsvConfirmationSetting();

  useEffect(() => {
    if (drawId) {
      setCurrentDrawId(drawId);
    }

    if (!hasUploadedFile && !hasCsvEntryConfirmationSetting) {
      reset();
      setIsAdvancedFormValid(false);
    }
  }, [
    drawId,
    setCurrentDrawId,
    reset,
    hasUploadedFile,
    hasCsvEntryConfirmationSetting,
  ]);

  const handleFileRemove = () => {
    deleteFile();
    setIsAdvancedFormValid(false);
  };

  const handleNext = () => {
    commitToDraw();
    onNext();
  };

  const getValidity = (): boolean => {
    if (!isUploaded || isUploading || hasError) return false;

    if (headerRowAnswer === false) return false;

    if (entryMethod === "standard") {
      return headerRowAnswer === true;
    } else if (entryMethod === "advanced") {
      return isAdvancedFormValid && headerRowAnswer === true;
    }

    return false;
  };

  const canProceed = getValidity();

  const dataHygieneNote = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--plexus-space-sm, 8px)",
      }}
      data-testid="entries-data-hygiene-note"
    >
      <PlxsIcon name="infoCircle" size="sm" />
      <PlxsText size="sm">
        Only include the data you need. Stripping out emails and addresses keeps
        things clean.
      </PlxsText>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--plexus-space-md, 16px)",
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <WizardStepHeader
        title="Upload your entries"
        description="Upload all eligible entries – we'll pick the winners at random."
        badgeLabel={toSentenceCase(entryMethod)}
        badgeTone={entryMethod === "standard" ? "brand" : "caution"}
      />

      {!isUploaded && (
        <SmartUploadComponent
          entryMethod={entryMethod}
          drawId={drawId}
          disabled={isUploading}
        />
      )}

      {(entriesUpload?.originalFileName || hasError) && (
        <FilePreview onRemove={handleFileRemove} />
      )}

      {dataHygieneNote}

      {isUploaded &&
        hasCsvEntryConfirmationSetting &&
        entryMethod === "standard" && (
          <div style={{ marginTop: "var(--plexus-space-sm, 8px)" }}>
            <HeaderRowQuestion
              entriesUpload={entriesUpload}
              isDisabled={isLocked}
            />

            {headerRowAnswer === false && (
              <PlxsCallout
                dataTestId="no-header-row-warning"
                tone="urgent"
                message="Your file needs a header row – re-upload with column names in the first row."
              ></PlxsCallout>
            )}

            {headerRowAnswer === true && (
              <div style={{ marginTop: "var(--plexus-space-md, 16px)" }}>
                <ParticipantIdentificationQuestion
                  entriesUpload={entriesUpload}
                  isDisabled={isLocked}
                />
              </div>
            )}
          </div>
        )}

      {isUploaded &&
        hasCsvEntryConfirmationSetting &&
        entryMethod === "advanced" && (
          <div style={{ marginTop: "var(--plexus-space-sm, 8px)" }}>
            <AdvancedEntriesUpload
              onValidityChange={setIsAdvancedFormValid}
              isDisabled={isLocked}
            />
          </div>
        )}
      <WizardFooterNav
        backButton={{
          dataTestId: "back-upload",
          label: "Back",
          variant: "secondary",
          onClick: onBack,
          isDisabled: isUploading,
        }}
        continueButton={{
          dataTestId: "next-upload",
          label: isUploading ? "Processing..." : "Next",
          variant: "primary",
          onClick: handleNext,
          isDisabled: !canProceed,
        }}
      />
    </div>
  );
};

export default UploadEntriesStep;
